import { useEffect, useState } from "react";
import { Box, Typography, IconButton, Paper } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import PropTypes from "prop-types";
import theme from "../../theme";

const ContextualGuidance = ({ target, message, onClose }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [highlightStyle, setHighlightStyle] = useState({});
  const [showBelow, setShowBelow] = useState(false);

  useEffect(() => {
    if (!target) return;

    const updatePosition = (shouldScroll = false) => {
      const element = document.querySelector(target);
      if (element) {
        const rect = element.getBoundingClientRect();
        const TOOLTIP_HEIGHT = 150; // Approximate tooltip height
        const SPACING = 20; // Space between tooltip and element
        const TOOLTIP_WIDTH = 400; // Max width of tooltip
        const EDGE_MARGIN = 20; // Margin from screen edges

        // Check if there's enough space above the element
        const spaceAbove = rect.top;
        const shouldShowBelow = spaceAbove < TOOLTIP_HEIGHT + SPACING;
        setShowBelow(shouldShowBelow);

        // Calculate horizontal position, ensuring tooltip stays within viewport
        let leftPosition = rect.left + window.scrollX + rect.width / 2;
        const halfTooltipWidth = TOOLTIP_WIDTH / 2;

        // Adjust if tooltip would go off left edge
        if (leftPosition - halfTooltipWidth < EDGE_MARGIN) {
          leftPosition = halfTooltipWidth + EDGE_MARGIN;
        }
        // Adjust if tooltip would go off right edge
        if (leftPosition + halfTooltipWidth > window.innerWidth - EDGE_MARGIN) {
          leftPosition = window.innerWidth - halfTooltipWidth - EDGE_MARGIN;
        }

        // Calculate vertical position
        let topPosition;
        if (shouldShowBelow) {
          // Position below the element
          topPosition = rect.bottom + window.scrollY + SPACING;
        } else {
          // Position above the element
          topPosition = rect.top + window.scrollY - SPACING;
        }

        setPosition({
          top: topPosition,
          left: leftPosition,
        });

        // Set highlight style to match element position
        setHighlightStyle({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height,
        });

        // Only scroll into view on initial load when explicitly requested
        if (shouldScroll) {
          // For large elements, scroll to show the top of the element
          if (rect.height > window.innerHeight * 0.6) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          } else {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      }
    };

    // Initial position WITH scroll
    updatePosition(true);

    // Update on window resize or scroll WITHOUT scrolling
    const handleResize = () => updatePosition(false);
    const handleScroll = () => updatePosition(false);

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true); // Use capture to handle all scroll events

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [target]);

  if (!target) return null;

  return (
    <>
      {/* Dark overlay */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 9997,
        }}
        onClick={onClose}
      />

      {/* Highlight box around target element */}
      <Box
        sx={{
          position: "absolute",
          top: highlightStyle.top,
          left: highlightStyle.left,
          width: highlightStyle.width,
          height: highlightStyle.height,
          border: `3px solid ${theme.palette.primary.main}`,
          borderRadius: "8px",
          boxShadow: `0 0 0 4px ${theme.palette.primary.main}40, 0 0 20px ${theme.palette.primary.main}`,
          zIndex: 9998,
          pointerEvents: "none",
          animation: "pulse 2s infinite",
          "@keyframes pulse": {
            "0%": {
              boxShadow: `0 0 0 4px ${theme.palette.primary.main}40, 0 0 20px ${theme.palette.primary.main}`,
            },
            "50%": {
              boxShadow: `0 0 0 8px ${theme.palette.primary.main}60, 0 0 30px ${theme.palette.primary.main}`,
            },
            "100%": {
              boxShadow: `0 0 0 4px ${theme.palette.primary.main}40, 0 0 20px ${theme.palette.primary.main}`,
            },
          },
        }}
      />

      {/* Guidance message tooltip */}
      <Paper
        elevation={8}
        sx={{
          position: "absolute",
          top: position.top,
          left: position.left,
          transform: showBelow ? "translate(-50%, 0)" : "translate(-50%, -100%)",
          backgroundColor: theme.palette.primary.main,
          color: "#ffffff",
          padding: "16px 20px",
          borderRadius: "12px",
          maxWidth: "400px",
          zIndex: 9999,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Typography variant="body2" sx={{ flex: 1, color: "#ffffff", lineHeight: 1.6 }}>
            {message}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {/* Arrow pointing to element */}
        <Box
          sx={{
            position: "absolute",
            ...(showBelow
              ? {
                  top: -8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                  borderLeft: "8px solid transparent",
                  borderRight: "8px solid transparent",
                  borderBottom: `8px solid ${theme.palette.primary.main}`,
                }
              : {
                  bottom: -8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                  borderLeft: "8px solid transparent",
                  borderRight: "8px solid transparent",
                  borderTop: `8px solid ${theme.palette.primary.main}`,
                }),
          }}
        />
      </Paper>
    </>
  );
};

ContextualGuidance.propTypes = {
  target: PropTypes.string, // CSS selector for the target element
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ContextualGuidance;
